import { ChangeDetectionStrategy, Component, OnChanges, Input } from '@angular/core';
import { OrderUtilityService } from './../../../services';
import { ErrorMessage } from './../../../models';
import { ErrorType } from './../../../models/enums';
import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import { PopupTooltipComponent } from '../../../modules/insight-popup-tooltip/components/insight-popup-tooltip/popup-tooltip.component';
import { MatIcon } from '@angular/material/icon';
import { ExpandingErrorComponent } from './expanding-error/expanding-error.component';
import { ErrorGroupView } from './error-group-view';
import { escapeHtml } from '../../utilities/escape-html';
import { inject } from '@angular/core';

@Component({
  selector: 'insight-order-issues',
  templateUrl: './order-issues.component.html',
  styleUrls: ['./order-issues.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CdkOverlayOrigin, PopupTooltipComponent, MatIcon, ExpandingErrorComponent]
})
export class OrderIssuesComponent implements OnChanges {
  // == DEPENDENCIES =====================
  private readonly orderUtilityService = inject(OrderUtilityService);

  // == CONSTANTS ========================
  // The limit of how many characters we show in a message before cutting
  // off and showing the rest in an <expanding-error/>
  private readonly maxFullMessageSize: number = 300;

  // == STATE ============================
  @Input() errors: ErrorMessage[];
  @Input() showIssuesLabel = true;

  public flattenedErrors: ErrorMessage[] = [];
  public groupedErrors: Record<ErrorType, ErrorMessage[]> = {
    [ErrorType.All]: [],
    [ErrorType.FileSizeError]: [],
    [ErrorType.Hd3Error]: [],
    [ErrorType.MissingContent]: [],
    [ErrorType.MissingPublicity]: [],
    [ErrorType.MissingTrailer]: [],
    [ErrorType.ScheduleError]: [],
    [ErrorType.PastShipDate]: [],
    [ErrorType.ThresholdViolation]: [],
    [ErrorType.Unknown]: [],
  };

  // view models precomputed once per component instance
  public errorGroups: ErrorGroupView[] = [];
  public cdkOverlayOrigin: CdkOverlayOrigin;

  // == LIFE CYCLE =======================
  ngOnChanges() {
    this.getGroups().forEach(group => this.groupedErrors[group] = []);

    // organize all the errors by group
    this.errors?.forEach((e: ErrorMessage) => {
      this.groupedErrors[e.type].push(e);
    });

    this.errorGroups = this.getGroups()
      .filter(group => this.groupedErrors[group].length > 0)
      .map(group => ({
        abbreviation: this.orderUtilityService.getErrorAbbreviation(group),
        typeName: this.orderUtilityService.mapOrderErrorType(+group),
        errors: this.groupedErrors[+group].map((error: ErrorMessage) => ({
          lineNumber: error.lineNumber,
          showSimple: this.showingSimpleMessage(error),
          simpleMessage: this.simpleMessage(error),
          simpleMessageInfo: this.simpleMessageInfo(error),
          fullMessage: error.message,
          formattedMessage: this.formatErrorMessage(error)
        }))
      }));
  }

  // == HELPERS ==========================
  /**
   * Get each group in groupedErrors.
   * @returns The groups of groupedErrors.
   */
  public getGroups(): Array<string> {
    return Object.keys(this.groupedErrors);
  }

  /**
   * If we should show the simple message with an expander to the full message. 
   * @param error The error.
   * @returns If we should show the simple message.
   */
  private showingSimpleMessage(error: ErrorMessage): boolean {
    return error.simpleMessage?.length > 0 || error.message.length > this.maxFullMessageSize;
  }

  /**
   * The simple message to display. If we already have one from the backend or if
   * we're making one here by cutting off all text from the full message past the maxFullMessageSize.
   * @param error The error.
   * @returns The simple message.
   */
  private simpleMessage(error: ErrorMessage): string {
    return error.simpleMessage?.length > 0 ? error.simpleMessage : error.message.substring(0, this.maxFullMessageSize);
  }

  /**
   * If we are not just taking the plain simpleMessage from the backend, a string saying what additional things we did to the full message
   * to make the simple message. For now, this is just how many characters we cut off past the max full message size. If not, null.
   * @param error The error message.
   * @returns The additional info on the simple message.
   */
  private simpleMessageInfo(error: ErrorMessage): string {
    return error.simpleMessage?.length > 0 ? null : `(${error.message.length - this.maxFullMessageSize} more characters...)`;
  }

  /**
   * Format the error message using HTML for better display- turning URLs into links and \n into
   * actual newlines.
   * @param message The raw string to make into HTML.
   * @returns The output HTML.
   */
  private formatErrorMessage(message: ErrorMessage): string {
    // escape any HTML characters in the raw message
    let messageText = escapeHtml(message.message);

    // if someone put a link in the message, convert it to HTML
    if (messageText.includes('http://') || messageText.includes('https://')) {
      messageText = messageText.replace(/ +(?= )/g, ''); // get rid of all multiple white space

      // break apart message into chunks. We can swap out the URL easier that way
      // rebuild the message as we figure out what needs to be updated
      let stringBuilder = '';
      const messageChunks = messageText.split(' ');
      messageChunks.forEach((chunk, index) => {
        if (chunk.startsWith('http')) {
          messageChunks[index] = `<a href="${chunk}" target="_blank">${chunk}</a>`;
        }
        stringBuilder = `${stringBuilder} ${messageChunks[index]}`;
      });

      messageText = stringBuilder; // assign the final updated message value for the return
    }

    // make it so newlines in the errors are actually new lines
    return messageText.split('\n').map(line => `<span>${line}</span>`).join('');
  }
}
